import { SidebarItem } from '@backstage/core-components';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { noticesReadPermission } from '@internal/backstage-plugin-neo4j-notices-common';
import { SmallInfoChip } from '@internal/backstage-plugin-neo4j-ui-kit-react';
import { RiAlarmWarningLine } from '@remixicon/react';
import { NOTICES_BASE_PATH } from '../../const';
import { useNotices } from '../../hooks/useNotices';
import { QueryClientProvider } from '../QueryClientProvider';

const MAX_COUNT_SHOWN = 9;

/**
 * Sidebar item with a chip holding the number of published notices,
 * dismissed or not, so that an admin who dismissed a notice still sees that
 * it is live for everyone else. The chip uses the same `sidebar-item-with-chip`
 * slot the Environments beta item used. Use it inside a `QueryClientProvider`
 * where the caller gates the permission; use `NoticesSidebarItem` otherwise.
 */
export const NoticesSidebarItemContent = () => {
  const { data } = useNotices({ poll: true });
  const count = data?.length ?? 0;

  return (
    <span className="sidebar-item-with-chip">
      <SidebarItem icon={NoticesIcon} to={NOTICES_BASE_PATH} text="Notices">
        {count > 0 && <SmallInfoChip label={formatCount(count)} color="red" />}
      </SidebarItem>
    </span>
  );
};

/**
 * Sidebar entry for the notices admin page. It renders only for users with
 * the `neo4j-notices.notices.read` permission. Use it in the app sidebar; it
 * brings its own provider and permission gate.
 */
export const NoticesSidebarItem = () => (
  <RequirePermission permission={noticesReadPermission} errorPage={<></>}>
    <QueryClientProvider>
      <NoticesSidebarItemContent />
    </QueryClientProvider>
  </RequirePermission>
);

function formatCount(count: number) {
  return count > MAX_COUNT_SHOWN ? `${MAX_COUNT_SHOWN}+` : String(count);
}

function NoticesIcon() {
  return <RiAlarmWarningLine size={20} />;
}
